import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useAppStore } from '@/stores/useAppStore';

type UserRole = 'patient' | 'practitioner' | 'admin' | 'moderator';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  roles: UserRole[];
  isPatient: boolean;
  isAdmin: boolean;
  isModerator: boolean;
  loading: boolean;
  refreshAuth: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signUp: (email: string, password: string, pseudo?: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: any }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { user, setUser, isLoading: loading, setLoading } = useAppStore();
  const [session, setSession] = useState<Session | null>(null);
  const [roles, setRoles] = useState<UserRole[]>([]);

  const loadUserData = async (userId: string) => {
    try {
      const { data: rolesData } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId);

      const userRoles = rolesData?.map(r => r.role as UserRole) || [];

      if (userRoles.length === 0) {
        userRoles.push('patient');
      }

      setRoles(userRoles);
    } catch (error) {
      console.error('Error loading user data:', error);
      setRoles(['patient']);
    }
  };

  const refreshAuth = async () => {
    try {
      const { data: { session: currentSession }, error } = await supabase.auth.getSession();

      if (error) throw error;

      setSession(currentSession);
      setUser(currentSession?.user || null);

      if (currentSession?.user) {
        await loadUserData(currentSession.user.id);
      } else {
        setRoles([]);
      }
    } catch (error) {
      console.error('Error refreshing auth:', error);
      setSession(null);
      setUser(null);
      setRoles([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadingTimeout = setTimeout(() => {
      if (loading) {
        setLoading(false);
      }
    }, 1000);

    refreshAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, currentSession) => {
        setSession(currentSession);
        setUser(currentSession?.user || null);

        if (currentSession?.user) {
          await loadUserData(currentSession.user.id);
        } else {
          setRoles([]);
        }

        setLoading(false);
      }
    );

    return () => {
      clearTimeout(loadingTimeout);
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (!error) {
      await refreshAuth();
    }
    return { error };
  };

  const signUp = async (email: string, password: string, pseudo?: string) => {
    const redirectUrl = `${window.location.origin}/`;
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: { pseudo: pseudo || email.split('@')[0] },
      },
    });
    return { error };
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      toast.error(error.message);
    }
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    return { error };
  };

  const value: AuthContextType = {
    user,
    session,
    roles,
    isPatient: roles.includes('patient'),
    isAdmin: roles.includes('admin'),
    isModerator: roles.includes('moderator'),
    loading,
    refreshAuth,
    signIn,
    signUp,
    signOut,
    resetPassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
